import { addDays } from '@dairy/shared';
import { live, type DomainEvent } from './types';

export interface WithdrawalWindow {
  from: string;
  to: string;
  eventId: string;
}

/**
 * D-6: each TREATMENT withholds milk from its date to (last treatment date or date) + milkWithdrawalDays,
 * inclusive. 0 days = no withdrawal.
 */
export function withdrawalWindows(events: readonly DomainEvent[]): WithdrawalWindow[] {
  const out: WithdrawalWindow[] = [];
  for (const e of live(events)) {
    if (e.type !== 'TREATMENT') continue;
    const d = e.data as { milkWithdrawalDays?: number; lastDate?: string };
    const days = d.milkWithdrawalDays ?? 0;
    if (days <= 0) continue;
    const last = d.lastDate && d.lastDate > e.date ? d.lastDate : e.date;
    out.push({ from: e.date, to: addDays(last, days), eventId: e.id });
  }
  return out.sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));
}

/** Overlapping or adjacent windows merged: the latest end wins. */
export function mergeWindows(windows: readonly WithdrawalWindow[]): WithdrawalWindow[] {
  const sorted = [...windows].sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));
  const out: WithdrawalWindow[] = [];
  for (const w of sorted) {
    const last = out[out.length - 1];
    if (last && w.from <= addDays(last.to, 1)) {
      if (w.to > last.to) {
        last.to = w.to;
        last.eventId = w.eventId;
      }
    } else {
      out.push({ ...w });
    }
  }
  return out;
}

/** Treatment withdrawal covering `date` for one animal's events, or null. */
export function activeWithdrawal(
  animalEvents: readonly DomainEvent[],
  date: string,
): { until: string; eventId: string } | null {
  for (const w of mergeWindows(withdrawalWindows(animalEvents))) {
    if (w.from <= date && date <= w.to) return { until: w.to, eventId: w.eventId };
  }
  return null;
}
